'use client';
import { createContext, useContext, useEffect, useId, useLayoutEffect, useRef, type ReactNode } from 'react';
import { claimOwnerHome, registerToastOutlet, ROOT_TOAST_SCOPE } from './overlayStack';

/** Which surface a hook was called under. A dialog, drawer, sheet or page overlay is one surface: every
 *  autosave confirmation inside it shares one saved card, and an actionable toast raised from it can act
 *  only while it is the active surface. Hooks called ABOVE a surface see the enclosing one — place the
 *  hook inside the surface it reports for. */
export const ToastScopeContext = createContext<string>(ROOT_TOAST_SCOPE);
export function useToastScope(): string {
  return useContext(ToastScopeContext);
}

/** Call inside a surface that hosts the editing of ONE writer instance: `owner` is the `resource` that
 *  writer's `useAutosaveToast` was given, and must identify the instance (a draft exposes `draft.owner`), not the
 *  thing it edits, or two writers of one thing would move together. While the surface is open, that writer's
 *  saved card and recovery cards are raised in it, so their actions are reachable there. See `claimOwnerHome`. */
export function useOwnerHome(owner: string): void {
  const scope = useToastScope();
  useEffect(() => claimOwnerHome(owner, scope), [owner, scope]);
}

/** `useOwnerHome` as an element, for a surface that takes the owner as a prop (`Modal.reportedBy`,
 *  `ConfirmDialog.reportedBy`): rendered inside the surface, so the claim is made in the surface's own scope. */
export function OwnerHome({ owner }: { owner: string }) {
  useOwnerHome(owner);
  return null;
}

/** The toast side of a modal surface, rendered by `DialogContent` and `AlertDialogContent` once for every
 *  overlay: it states the surface's scope and registers a toast outlet inside the surface's own content,
 *  i.e. inside its focus scope. On desktop it is viewport-fixed, like page feedback. On phones CSS orders
 *  it directly after the header and reserves the stack's height, so persistent cards cannot cover the
 *  title, close control or answers. It stays LAST in the DOM (and tab order) in both layouts.
 *  Desktop surfaces use an opacity-only entrance: a transform, even an identity transform retained by an
 *  animation, would trap the fixed toaster in the surface instead of the viewport.
 *
 *  A surface handed to `DialogContent asChild` has nowhere for the wrapper to add the outlet, so its owner
 *  renders `<ToastSurface />` (no children) as the LAST child of the element it hands over; the contract
 *  guard rejects an `asChild` surface that does not. */
export function ToastSurface({ children }: { children?: ReactNode }) {
  const scope = useId();
  const outlet = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => outlet.current ? registerToastOutlet({ node: outlet.current, scope }) : undefined, [scope]);
  return (
    <ToastScopeContext value={scope}>
      {children}
      <div
        ref={outlet}
        data-slot="toast-outlet"
        className="z-[var(--z-toast)]"
      />
    </ToastScopeContext>
  );
}
